import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import Badge from "../../Components/Badge";
import { requestGetTransaction } from "../../Context/transaction/actions";
import {
  useTransactionDispatch,
  useTransactionState,
} from "../../Context/transaction/context";
import {
  pathBuilder,
  paymentMethodName,
  productTypeName,
  convertToGoogleAnalyticsItem,
  trackPurchase,
} from "../../Utils";
import { Button } from "../../Components/Button";
import DetailSkeleton from "../../Components/DetailSkeleton";
import { Alert } from "../../Components/Alert";
import { CSButton } from "../../Components/CSButton";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";

// Resolve the consultation chat id a product row should link to, or null when
// it can't be resolved (no link rendered — never route to a catalog id).
// Add-on rows in a combined purchase carry the catalog id ("video-addon-v3",
// "zoom-addon", "audio-addon-v3") — the chat id lives on the sibling
// consultation product. Extend add-on rows embed the consultation id:
// video-<cid>-<versionId> (audio extends keep the video- prefix) or zoom-<cid>.
const consultationLinkId = (product, products) => {
  if (product.type === "consultation") {
    if (product.id?.includes("addon")) {
      return (
        products.find(
          (p) => p.type === "consultation" && !p.id?.includes("addon"),
        )?.id || null
      );
    }
    return product.id;
  }
  if (product.type === "consultation-extend") {
    const cid = product.id
      .replace(/^(video-|zoom-)/, "")
      .replace(/-(video-addon(-v\d+)?|audio-addon-v3)$/, "");
    // A leftover catalog fragment means an unrecognized add-on format.
    return cid && !cid.includes("addon") ? cid : null;
  }
  return product.id;
};

const TableHead = () => {
  const { t } = useTranslation();

  return (
    <thead className="bg-gray-50">
      <tr>
        <th
          scope="col"
          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
        >
          {t("#")}
        </th>
        <th
          scope="col"
          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
        >
          {t("Name")}
        </th>
        <th
          scope="col"
          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
        >
          {t("Quantity")}
        </th>
        <th
          scope="col"
          className="px-3 py-3.5 text-sm font-semibold text-right text-gray-900"
        >
          {t("Price")}
        </th>
        <th
          scope="col"
          className="px-3 py-3.5 text-sm font-semibold text-right text-gray-900"
        >
          {t("Total Price")}
        </th>
      </tr>
    </thead>
  );
};

const TableBodySkeleton = () => (
  <tbody className="animate-pulse divide-y divide-gray-200 bg-white">
    <tr>
      <td className="px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
      <td className="px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
      <td className="flex justify-end px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
    </tr>
    <tr>
      <td className="px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
      <td className="px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
      <td className="flex justify-end px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
    </tr>
    <tr>
      <td className="px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
      <td className="px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
      <td className="flex justify-end px-3 py-4">
        <div className="h-3 w-14 bg-gray-300 rounded-md"></div>
      </td>
    </tr>
  </tbody>
);

const PENDING_REFRESH_INTERVAL_MS = 10_000;
const MAX_PENDING_REFRESHES = 30;

const TransactionDetail = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useTransactionDispatch();
  const { transaction, loading, error } = useTransactionState();
  const [subtotal, setsubtotal] = useState(0);
  const pendingRefresh = useRef({ transactionId: null, count: 0 });

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      requestGetTransaction(dispatch, { id });
    }
  }, [id, dispatch]);

  useEffect(() => {
    pendingRefresh.current = { transactionId: id, count: 0 };
  }, [id]);

  useEffect(() => {
    if (
      !id ||
      transaction?.id !== id ||
      transaction?.status !== "PENDING"
    ) {
      return;
    }

    let inFlight = false;
    const refreshTransaction = async () => {
      if (
        document.visibilityState !== "visible" ||
        inFlight ||
        pendingRefresh.current.count >= MAX_PENDING_REFRESHES
      ) {
        return;
      }

      inFlight = true;
      pendingRefresh.current.count += 1;
      try {
        await requestGetTransaction(dispatch, { id, silent: true });
      } finally {
        inFlight = false;
      }
    };

    const intervalId = window.setInterval(
      refreshTransaction,
      PENDING_REFRESH_INTERVAL_MS,
    );
    document.addEventListener("visibilitychange", refreshTransaction);
    window.addEventListener("focus", refreshTransaction);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshTransaction);
      window.removeEventListener("focus", refreshTransaction);
    };
  }, [dispatch, id, transaction?.id, transaction?.status]);

  useEffect(() => {
    let totalPrice = 0;
    let subtotalPrice = 0;

    if (transaction?.products && transaction?.products.length > 0) {
      transaction.products.forEach((product) => {
        totalPrice = product.price * product.quantity;
        subtotalPrice += totalPrice;
      });
    }
    setsubtotal(subtotalPrice);
  }, [transaction.products]);

  // Track purchase for successful transactions
  useEffect(() => {
    if (
      transaction?.id &&
      transaction?.status === "SUCCESS" &&
      transaction?.products
    ) {
      // Check 24-hour window from updatedAt
      const now = dayjs();
      const updatedAt = dayjs(transaction.updatedAt);
      const hoursDiff = now.diff(updatedAt, "hour");

      if (hoursDiff <= 24) {
        // Check localStorage for deduplication
        const successTransactionsStr = localStorage.getItem(
          "successTransactions",
        );
        let transactionIds = [];

        if (successTransactionsStr) {
          try {
            transactionIds = JSON.parse(successTransactionsStr);
          } catch (e) {
            transactionIds = [];
          }
        }

        // Only track if not already tracked
        if (!transactionIds.includes(transaction.id)) {
          // Track purchase with Google Analytics
          const analyticsParams = {
            transaction_id: transaction.id,
            currency: "IDR",
            value: transaction.totalPay,
            items: transaction.products.map((product) =>
              convertToGoogleAnalyticsItem(product),
            ),
          };

          if (transaction.voucher) {
            analyticsParams.coupon = transaction.voucher;
          }

          logEvent(analytics, "purchase", analyticsParams);

          // Track purchase with Meta Pixel
          trackPurchase({
            products: transaction.products,
            transactionId: transaction.id,
            value: transaction.totalPay,
          });

          // Update localStorage to prevent duplicate tracking
          transactionIds.push(transaction.id);
          localStorage.setItem(
            "successTransactions",
            JSON.stringify(transactionIds),
          );
        }
      }
    }
  }, [transaction]);

  function isClosingSoon(expiredAt) {
    const currentTime = dayjs();
    const expirationTime = dayjs(expiredAt);
    const tenMinutesBeforeExpiration = expirationTime.subtract(10, "minutes");

    return (
      currentTime.isAfter(tenMinutesBeforeExpiration) &&
      currentTime.isBefore(expirationTime)
    );
  }

  function setButtonLabel(product, t, productTypeName) {
    switch (product.type) {
      case "emeterai":
        return `${t("Use")} ${t(productTypeName[product.type])}`;
      case "scheduled-tutoring":
        return t("View Tutoring Program");
      case "business-service":
        return t("viewOrder");
      case "webinar":
        return t("webinars.actions.view");
      default:
        return productTypeName[product.type]
          ? `${t("View")} ${t(productTypeName[product.type])}`
          : t("View");
    }
  }

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <div className="bg-white shadow-sm min-h-full rounded-lg p-4">
        {transaction.paymentType === "briva" &&
          !error &&
          transaction.status === "PENDING" && (
            <Alert
              type="info"
              className="mb-8"
              message="Pembayaran menggunakan BRI Virtual Account diproses dalam 5 menit."
            />
          )}
        {error ? (
          <Alert
            type="error"
            title="Error"
            message={error?.message || "Error fetching transaction."}
            errors={error?.errors}
          />
        ) : (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="flex justify-start">
                <span className="text-sm text-gray-500">{t("Status")}</span>
              </dt>
              <dd className="flex justify-start">
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <div>
                    {transaction.status === "PENDING" ? (
                      <Badge title="Pending" type="alert" />
                    ) : null}
                    {transaction.status === "SUCCESS" ? (
                      <Badge title="Success" type="success" />
                    ) : null}
                    {transaction.status === "FAILED" ? (
                      <Badge title="Failed" type="danger" />
                    ) : null}
                    {transaction.status === "EXPIRED" ? (
                      <Badge title="Expired" type="secondary" />
                    ) : null}
                    {transaction.status === "REFUNDED" ? (
                      <Badge title="Refunded" type="secondary" />
                    ) : null}
                  </div>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="flex justify-start sm:justify-end">
                <span className="text-sm text-gray-500">
                  {t("Creation Date")}
                </span>
              </dt>
              <dd className="flex justify-start sm:justify-end">
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <div className="text-sm font-bold text-gray-900">
                    {dayjs(transaction.createdAt)
                      .locale(i18n.language)
                      .format("DD MMM YYYY HH:mm")}
                  </div>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="flex justify-start">
                <span className="text-sm text-gray-500">
                  {t("Payment Method")}
                </span>
              </dt>
              <dd className="flex justify-start">
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <div className="text-sm font-bold text-gray-900">
                    {paymentMethodName[transaction.paymentType] ||
                      transaction.paymentType}
                  </div>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1 text-left sm:text-right">
              <dt className="flex justify-start sm:justify-end">
                {!transaction.mandiriBill && (
                  <span className="text-sm text-gray-500">
                    {t("Payment Number")}
                  </span>
                )}
              </dt>
              <dd className="flex justify-start sm:justify-end">
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <div>
                    {transaction.status === "PENDING" ? (
                      <div>
                        {transaction.redirect && (
                          <div className="text-sm text-blue-600 underline">
                            <a
                              href={transaction.redirect}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("Continue to Payment")}
                            </a>
                          </div>
                        )}
                        {transaction.gopay &&
                          transaction.paymentType === "gopay" && (
                            <div className="text-sm text-blue-600 underline">
                              <a
                                href={transaction.gopay}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("Pay using GoPay")}
                              </a>
                            </div>
                          )}
                        {transaction.paymentNumber &&
                          !transaction.redirect &&
                          !transaction.mandiriBill && (
                            <div className="text-sm font-bold text-gray-900">
                              <span>
                                {transaction.paymentNumber}
                                {transaction.paymentDestinationName
                                  ? ` (${transaction.paymentDestinationName})`
                                  : ""}
                              </span>
                            </div>
                          )}
                        {transaction.mandiriBill && !transaction.redirect && (
                          <div className="flex flex-col gap-2">
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-500">
                                {t("Kode Perusahaan")}
                              </span>
                              <span className="text-sm font-bold text-gray-900">
                                {transaction.mandiriBill.billerCode}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-500">
                                {t("Kode Pembayaran")}
                              </span>
                              <span className="text-sm font-bold text-gray-900">
                                {transaction.mandiriBill.billKey}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="flex justify-start">
                <span className="text-sm text-gray-500">{t("Total")}</span>
              </dt>
              <dd className="flex justify-start">
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <div className="text-sm font-bold text-gray-900">
                    <NumericFormat
                      displayType={"text"}
                      value={transaction.totalPay}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  </div>
                )}
              </dd>
            </div>
            <div className="sm:col-span-1 text-left sm:text-right">
              <dt className="flex justify-start sm:justify-end">
                <span className="text-sm text-gray-500">
                  {t("Expired Date")}
                </span>
              </dt>
              <dd className="flex justify-start sm:justify-end">
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <div className="flex flex-col">
                    <div className="text-sm font-bold text-gray-900">
                      {transaction.expiredAt &&
                      transaction.status === "PENDING" ? (
                        dayjs(transaction.expiredAt)
                          .locale(i18n.language)
                          .format("DD MMM YYYY HH:mm")
                      ) : (
                        <span className="text-sm font-normal">-</span>
                      )}
                    </div>
                    {isClosingSoon(transaction.expiredAt) &&
                      transaction.status === "PENDING" && (
                        <span className="text-sm font-medium text-amber-600">
                          {t("Expire Soon!")}
                        </span>
                      )}
                  </div>
                )}
              </dd>
            </div>
            {loading ? (
              <div className="sm:col-span-2">
                <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
                    <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                      <table className="min-w-full divide-y divide-gray-300">
                        <TableHead />
                        <TableBodySkeleton />
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="sm:col-span-2">
                {transaction.products ? (
                  <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
                      <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                        <table className="min-w-full divide-y divide-gray-300">
                          <TableHead />
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {transaction.products.map((product, index) => (
                              <tr key={product.id}>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500">
                                  {index + 1}
                                </td>
                                <td className="whitespace-pre-wrap px-3 py-4 text-sm">
                                  <p className="font-medium text-gray-900">
                                    {t(product.name)}
                                  </p>
                                  {product.detail?.description &&
                                    product.type === "scheduled-tutoring" && (
                                      <p className="text-gray-500">
                                        {t(product.detail.description)}
                                      </p>
                                    )}

                                  {(product.type === "ebilling" ||
                                    (product.type === "business-service" &&
                                      transaction.status === "SUCCESS") ||
                                    (product.type === "scheduled-tutoring" &&
                                      transaction.status === "SUCCESS") ||
                                    (product.type === "emeterai" &&
                                      transaction.status === "SUCCESS") ||
                                    (product.type === "webinar" &&
                                      transaction.status === "SUCCESS") ||
                                    (product.type === "consultation-extend" &&
                                      !!consultationLinkId(
                                        product,
                                        transaction.products,
                                      )) ||
                                    (product.type === "consultation" &&
                                      transaction.status === "SUCCESS" &&
                                      !!consultationLinkId(
                                        product,
                                        transaction.products,
                                      ))) && (
                                    <a
                                      href={
                                        product.type === "emeterai"
                                          ? pathBuilder(product.type)
                                          : pathBuilder(
                                              product.type,
                                              consultationLinkId(
                                                product,
                                                transaction.products,
                                              ),
                                            )
                                      }
                                      className="text-sm text-blue-600"
                                    >
                                      {setButtonLabel(
                                        product,
                                        t,
                                        productTypeName,
                                      )}
                                    </a>
                                  )}
                                </td>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500">
                                  {product.quantity}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">
                                  <NumericFormat
                                    displayType={"text"}
                                    value={product.price}
                                    thousandSeparator={"."}
                                    decimalSeparator={","}
                                    prefix={"Rp"}
                                  />
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">
                                  <NumericFormat
                                    displayType={"text"}
                                    value={product.price * product.quantity}
                                    thousandSeparator={"."}
                                    decimalSeparator={","}
                                    prefix={"Rp"}
                                  />
                                </td>
                              </tr>
                            ))}
                            <tr key="subtotal">
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                                {t("Subtotal")}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">
                                <NumericFormat
                                  displayType={"text"}
                                  value={subtotal}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              </td>
                            </tr>
                            {Boolean(transaction.discounts) &&
                              transaction.discounts.map((discount) => (
                                <tr key={discount.id}>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                                    {t(discount.title)}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-green-600">
                                    <NumericFormat
                                      displayType={"text"}
                                      value={-Math.abs(discount.total)}
                                      thousandSeparator={"."}
                                      decimalSeparator={","}
                                      prefix={"Rp"}
                                    />
                                  </td>
                                </tr>
                              ))}
                            {Boolean(transaction.totalDiscount) &&
                              !Boolean(transaction.discounts) && (
                                <tr key="discount">
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                                    {t("Discount")}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-green-600">
                                    <NumericFormat
                                      displayType={"text"}
                                      value={
                                        -Math.abs(transaction.totalDiscount)
                                      }
                                      thousandSeparator={"."}
                                      decimalSeparator={","}
                                      prefix={"Rp"}
                                    />
                                  </td>
                                </tr>
                              )}
                            <tr key="total">
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 text-sm text-gray-500"></td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm font font-medium text-right">
                                {t("Total")}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-right">
                                <NumericFormat
                                  displayType={"text"}
                                  value={transaction.totalPay}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
            {transaction.status === "PENDING" && (
              <div className="sm:col-span-2">
                <div className="flex flex-col items-end">
                  {transaction.redirect ? (
                    <a
                      href={transaction.redirect}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        color="blue"
                        loading={loading}
                        outline={Boolean(
                          transaction.gopay &&
                          transaction.paymentType === "gopay",
                        )}
                        title={t("Continue to Payment")}
                      />
                    </a>
                  ) : null}
                  {transaction.gopay && transaction.paymentType === "gopay" ? (
                    <a
                      href={transaction.gopay}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        color="blue"
                        loading={loading}
                        className="mt-2"
                        title={t("Pay using GoPay")}
                      />
                    </a>
                  ) : null}
                </div>
              </div>
            )}
          </dl>
        )}
      </div>
      <div className="bg-white shadow-sm rounded-lg p-4">
        <CSButton />
      </div>
    </div>
  );
};

export default TransactionDetail;
